import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { DEFAULT_INDEX_GROUP } from '@/config/constants';
import type { OtherGroupIndexEntry, OtherGroupIndexValues } from '@/entities/market';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';
import { fetchOtherGroupIndex } from '@/features/markets/api/otherGroupIndex.api';

export type GroupIndexSymbol = { key: SymbolKey; group: string; from?: string };

export type GroupIndexDataRecord = Partial<Record<string, OtherGroupIndexEntry[]>>;

export function priceValue(values: OtherGroupIndexValues | undefined): string | undefined {
  return values?.Index ?? values?.Mid;
}

function getDecimalPlaces(value: string): number {
  const dot = value.indexOf('.');
  return dot >= 0 ? value.length - dot - 1 : 0;
}

export function calculateDelta(current: string, previous: string): string | null {
  if (!current || !previous) return null;
  const scale = getDecimalPlaces(current);
  return (Number.parseFloat(current) - Number.parseFloat(previous)).toFixed(scale);
}

export type RowSymbolSource<TRow> = (row: TRow) => GroupIndexSymbol[];

function fetchableSymbolKey(sym: GroupIndexSymbol): string | null {
  if (sym.group === DEFAULT_INDEX_GROUP || !sym.key.Code) return null;
  return symbolKey(sym.key);
}

function appendUniqueFetchableSymbol(
  symbols: GroupIndexSymbol[],
  seen: Set<string>,
  sym: GroupIndexSymbol
): void {
  const key = fetchableSymbolKey(sym);
  if (!key || seen.has(key)) return;

  seen.add(key);
  symbols.push({ key: sym.key, group: sym.group, from: sym.from });
}

export function collectFetchableSymbols<TRow>(
  sections: { rows: TRow[] }[] | undefined,
  getRowSymbols: RowSymbolSource<TRow>
): GroupIndexSymbol[] {
  if (!sections) return [];
  const symbols: GroupIndexSymbol[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    for (const row of section.rows) {
      for (const sym of getRowSymbols(row)) {
        appendUniqueFetchableSymbol(symbols, seen, sym);
      }
    }
  }
  return symbols;
}

export function useEnrichedSections<TRow, TSection extends { rows: TRow[] }>(
  sections: TSection[] | undefined,
  getRowSymbols: RowSymbolSource<TRow>,
  enrichRow: (row: TRow, dataRecord: GroupIndexDataRecord) => TRow
): { sections: TSection[] | undefined; isLoading: boolean } {
  const symbolsToFetch = useMemo(
    () => collectFetchableSymbols(sections, getRowSymbols),
    // getRowSymbols is expected to be module-scope stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections]
  );

  const { dataRecord, isLoading } = useGroupIndexQueries(symbolsToFetch);

  const enrichedSections = useMemo(() => {
    if (!sections) return undefined;
    if (symbolsToFetch.length === 0) return sections;
    return sections.map(section => ({
      ...section,
      rows: section.rows.map(row => enrichRow(row, dataRecord))
    }));
    // enrichRow is expected to be module-scope stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, symbolsToFetch, dataRecord]);

  return { sections: enrichedSections, isLoading };
}

export function useGroupIndexQueries(symbolsToFetch: GroupIndexSymbol[]): {
  dataRecord: GroupIndexDataRecord;
  isLoading: boolean;
} {
  return useQueries({
    queries: symbolsToFetch.map(sym => ({
      queryKey: ['otherGroupIndex', sym.group, symbolKey(sym.key), sym.from ?? null],
      queryFn: () => fetchOtherGroupIndex(sym.key, sym.group, { from: sym.from }),
      staleTime: 300_000
    })),
    combine: queryResults => {
      const record: GroupIndexDataRecord = {};
      symbolsToFetch.forEach((sym, i) => {
        const data = queryResults[i]?.data;
        if (data) record[symbolKey(sym.key)] = data;
      });
      return {
        dataRecord: record,
        isLoading: queryResults.some(r => r.isLoading)
      };
    }
  });
}
